import React, { useState, useEffect, useCallback, useRef } from "react";
import { IconArrowUp, IconArrowDown } from "@tabler/icons-react";
import MoonLoader from "react-spinners/MoonLoader";
import { useCRM } from "../CRMContext";
import { getContacts } from "../services/contacts.service";
import { formatPhoneNumber } from "../../../../helpers/Helpers";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR");
};

const COLUMNS = [
  { key: "name", label: "Nome", sortable: true },
  { key: "phone", label: "Telefone" },
  { key: "email", label: "E-mail" },
  { key: "column_name", label: "Estágio" },
  { key: "deal_value", label: "Valor", sortable: true },
  { key: "priority", label: "Prioridade", sortable: true },
  { key: "owner_name", label: "Responsável" },
  { key: "days_in_stage", label: "Dias", sortable: true },
  { key: "created_at", label: "Criado", sortable: true },
];

const ListView = () => {
  const { state, openDrawer, toggleSelectContact } = useCRM();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("-created_at");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const scrollRef = useRef(null);

  const fetchList = useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const currentOffset = reset ? 0 : offset;
        const params = {
          pipeline: state.activePipelineId,
          offset: currentOffset,
          limit: 50,
          sort_by: sortBy,
          ...state.filters,
        };
        if (state.searchQuery) params.search = state.searchQuery;

        const resp = await getContacts(params);
        const results = resp.results || [];
        if (reset) {
          setContacts(results);
          setOffset(50);
        } else {
          setContacts((prev) => [...prev, ...results]);
          setOffset((prev) => prev + 50);
        }
        setHasMore(resp.has_next || false);
        setTotal(resp.count || 0);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    },
    [state.activePipelineId, state.filters, state.searchQuery, sortBy, offset]
  );

  useEffect(() => {
    fetchList(true);
  }, [state.activePipelineId, state.filters, state.searchQuery, sortBy]);

  // Infinite scroll
  const handleScroll = () => {
    if (!scrollRef.current || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchList(false);
    }
  };

  const handleSort = (key) => {
    if (sortBy === key) setSortBy(`-${key}`);
    else if (sortBy === `-${key}`) setSortBy(key);
    else setSortBy(`-${key}`);
  };

  const getSortIcon = (key) => {
    if (sortBy === key) return <IconArrowUp size={12} />;
    if (sortBy === `-${key}`) return <IconArrowDown size={12} />;
    return null;
  };

  // Map columns to names from context
  const columnNameMap = {};
  state.columns.forEach((col) => { columnNameMap[col.id] = { name: col.name, color: col.color || "#3b82f6" }; });

  return (
    <div className="crm-list" ref={scrollRef} onScroll={handleScroll}>
      <div style={{ padding: "12px 0", fontSize: 13, color: "var(--text-muted)" }}>
        {total} contatos encontrados
      </div>

      <table className="crm-list__table">
        <thead>
          <tr>
            <th className="crm-list__th" style={{ width: 40 }}>
              <input type="checkbox" />
            </th>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`crm-list__th ${sortBy === col.key || sortBy === `-${col.key}` ? "crm-list__th--sorted" : ""}`}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                {col.label} {col.sortable && getSortIcon(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => {
            const colInfo = columnNameMap[c.column] || {};
            const isSelected = state.selectedContacts.includes(c.id);
            return (
              <tr
                key={c.id}
                className={`crm-list__row ${c.is_stagnant ? "crm-list__row--stagnant" : ""}`}
                onClick={() => openDrawer(c.id)}
              >
                <td className="crm-list__td" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelectContact(c.id)}
                  />
                </td>
                <td className="crm-list__td" style={{ fontWeight: 600 }}>{c.name || "—"}</td>
                <td className="crm-list__td">{formatPhoneNumber(c.phone) || c.phone || "—"}</td>
                <td className="crm-list__td">{c.email || "—"}</td>
                <td className="crm-list__td">
                  {colInfo.name ? (
                    <span className="crm-list__stage-badge" style={{ background: colInfo.color }}>
                      {colInfo.name}
                    </span>
                  ) : "—"}
                </td>
                <td className="crm-list__td">{c.deal_value ? formatCurrency(c.deal_value) : "—"}</td>
                <td className="crm-list__td">
                  {c.priority && (
                    <span className={`crm-badge crm-badge--${c.priority === "high" ? "danger" : c.priority === "medium" ? "warning" : "success"}`}>
                      {c.priority === "high" ? "Alta" : c.priority === "medium" ? "Média" : "Baixa"}
                    </span>
                  )}
                </td>
                <td className="crm-list__td">{c.owner_name || "—"}</td>
                <td className="crm-list__td">{c.days_in_stage ?? "—"}</td>
                <td className="crm-list__td">{formatDate(c.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
          <MoonLoader color="#3b82f6" size={24} />
        </div>
      )}

      {!loading && contacts.length === 0 && (
        <div className="crm-empty">
          <span className="crm-empty__title">Nenhum contato encontrado</span>
          <span className="crm-empty__desc">Tente ajustar os filtros ou criar um novo contato.</span>
        </div>
      )}
    </div>
  );
};

export default ListView;
